import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  Typography,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import MessageTemplateService from '../../services/messageTemplateService';

// Constants from Django model
const CHANNEL_CHOICES = {
  sms: 'SMS',
  email: 'Email',
  voice: 'Voice',
  chat: 'Chat',
};

const CATEGORY_CHOICES = {
  awareness: 'Awareness',
  interest: 'Interest',
  decision: 'Decision',
  conversion: 'Conversion',
  lost: 'Lost',
};

const CreateTemplateDialog = ({ 
  open, 
  onClose, 
  accounts, 
  selectedAccount,
  onTemplateCreated,
  loading,
  setLoading,
  showSnackbar,
}) => {
  const [newTemplate, setNewTemplate] = useState({
    name: "",
    content: "",
    category: "awareness",
    channel: "sms",
    is_system_template: false,
    account: selectedAccount,
  });
  const [variableCategories, setVariableCategories] = useState([]);

  const fetchAvailableVariables = async () => {
    try {
      const categories = await MessageTemplateService.getAvailableVariables();
      setVariableCategories(categories);
    } catch (err) {
      console.error('Failed to fetch available variables:', err);
      showSnackbar('Failed to fetch available variables', 'error');
    }
  };

  useEffect(() => {
    if (open) {
      fetchAvailableVariables();
    }
  }, [open]);

  const handleCreateTemplate = async () => {
    try {
      setLoading(true);
      const templateData = {
        ...newTemplate,
        account: newTemplate.is_system_template ? null : selectedAccount,
      };
      const savedTemplate = await MessageTemplateService.createTemplate(templateData);
      onTemplateCreated(savedTemplate);
      onClose();
      setNewTemplate({
        name: "",
        content: "",
        category: "awareness",
        channel: "sms",
        is_system_template: false,
        account: selectedAccount,
      });
      showSnackbar('Template created successfully');
    } catch (err) {
      showSnackbar('Failed to create template', 'error');
    } finally {
      setLoading(false);
    }
  };

  const insertVariable = (categoryName, variableName) => {
    const variable = `{{${categoryName}.${variableName}}}`;
    setNewTemplate(prev => ({
      ...prev,
      content: prev.content + variable
    }));
  };

  const renderVariableList = () => {
    return variableCategories.map((category) => (
      <Accordion key={category.name}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`${category.name}-content`}
          id={`${category.name}-header`}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2">
              {category.name}
            </Typography>
            <Tooltip title={category.description}>
              <InfoIcon fontSize="small" color="action" />
            </Tooltip>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {category.variables.map((variable) => (
              <Tooltip 
                key={variable.name} 
                title={
                  <Box>
                    <Typography variant="body2">{variable.description}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Field: {variable.field_name}
                    </Typography>
                  </Box>
                }
              >
                <Chip
                  label={`{{${category.name}.${variable.name}}}`}
                  size="small"
                  onClick={() => insertVariable(category.name, variable.name)}
                  sx={{ 
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      color: 'primary.contrastText'
                    }
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    ));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Template</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Name"
            value={newTemplate.name}
            onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
            fullWidth
            required
          />
          <FormControl fullWidth>
            <InputLabel>Account</InputLabel>
            <Select
              value={newTemplate.account}
              onChange={(e) => setNewTemplate({ ...newTemplate, account: e.target.value })}
              label="Account"
              disabled={newTemplate.is_system_template}
            >
              {accounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {account.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Select
            label="Category"
            value={newTemplate.category}
            onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value })}
            fullWidth
            required
          >
            {Object.entries(CATEGORY_CHOICES).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </Select>
          <Select
            label="Channel"
            value={newTemplate.channel}
            onChange={(e) => setNewTemplate({ ...newTemplate, channel: e.target.value })}
            fullWidth
            required
          >
            {Object.entries(CHANNEL_CHOICES).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </Select>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Message Template
            </Typography>
            <TextField
              multiline
              rows={4}
              value={newTemplate.content}
              onChange={(e) => setNewTemplate({ ...newTemplate, content: e.target.value })}
              placeholder="Enter your template content. Use {{variable}} for personalization."
              fullWidth
              required
            />
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                Available Variables
                <Tooltip title="Click on a variable to insert it into your template">
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Typography>
              {renderVariableList()}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleCreateTemplate} 
          variant="contained"
          disabled={loading}
        >
          Create Template
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTemplateDialog; 